import React, { ChangeEvent, EventHandler, KeyboardEventHandler, useEffect, useRef, useState } from "react";
import { Button } from "../../ui/button";
import { MicIcon, SendIcon, SquareIcon, Paperclip } from "lucide-react";
import { ReactComponent as GoogleDriveIcon } from "../../../static/icons/google-drive.svg";
import { Textarea } from "../../ui/textarea";
import { DateTime } from "luxon";
import Spinner from "../../../components/ui/spinner";
import { LiveWaveform } from "../../ui/live-waveform";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui/tooltip";
import { useDropzone } from "react-dropzone";
import { cn } from "../../../lib/utils";
import { Input } from "../../ui/input";
import { useLiveAudioTranscription } from "../../../data/hooks/use-live-audio-transcription";
import { AttachedFilesList } from "./attached-files-list";
import { useUploadFileMutation } from "../../../data/mutations/upload-file";
import { useDeleteFileMutation } from "../../../data/mutations/delete-file";
import { useGoogleDrive } from "../../../data/hooks/use-google-drive";

const MAX_RECORDING_SECONDS = parseInt(process.env.REACT_APP_MAX_AUDIO_RECORDING_SECONDS ?? "300");

const SUPPORTED_FILE_EXTENSIONS = [".txt", ".md", ".docx", ".pdf", ".jpg", ".jpeg", ".png", ".webp"];
const MAX_FILE_SIZE = 15 * 1024 * 1024;
const MAX_FILE_SIZE_MB = 15;

const ACCEPTED_FILE_TYPES = {
    "text/plain": [".txt"],
    "text/markdown": [".md"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/webp": [".webp"],
};

export interface FileUpload {
    id: string;
    name: string;
    uploading: boolean;
    mimeType: string;
    source?: "google-drive";
}

function DragIndicator() {
    return (
        <div className="py-2 text-center text-sm text-primary">
            Drop your document here ({SUPPORTED_FILE_EXTENSIONS.join(", ")})
        </div>
    );
}

function AttachButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        disabled={disabled}
                        variant="link"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={onClick}
                        className="absolute bottom-0 right-0 rounded-md opacity-60 transition-opacity hover:opacity-100"
                    >
                        <Paperclip className="h-4 w-4 text-muted-foreground" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>
                    <p>
                        Upload files: PDFs, Word docs, markdown, text files, and images (PNG/JPEG/WebP) to provide more
                        information to the AI.
                    </p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

function GoogleDriveButton({
    onClick,
    disabled,
    loading,
}: {
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
}) {
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        disabled={disabled || loading}
                        variant="link"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={onClick}
                        className="absolute bottom-0 right-8 rounded-md opacity-80 transition-opacity hover:opacity-100"
                    >
                        <GoogleDriveIcon className="h-5 w-5" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>
                    <p>Attach from Google Drive</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

function InputContainer({ children }: { children: React.ReactNode }) {
    return <div className="relative flex-1">{children}</div>;
}

function VoiceInputButton(props: {
    onRecorded: (text: string) => void;
    onStreamChange: (stream: MediaStream | null) => void;
    disabled?: boolean;
}) {
    const transcription = useLiveAudioTranscription({
        maxRecordingSeconds: MAX_RECORDING_SECONDS,
        onTranscription: (text) => {
            props.onRecorded(text);
        },
        onStop: () => props.onStreamChange(null),
    });

    const isRecording = transcription.status === "recording";
    const secondsRemaining = MAX_RECORDING_SECONDS - transcription.recordingSeconds;
    const showWarning = isRecording && secondsRemaining <= 30;

    useEffect(() => {
        props.onStreamChange(transcription.stream);
    }, [transcription.stream]);

    if (isRecording) {
        return (
            <Popover open={showWarning}>
                <PopoverContent className={"mt-1 w-80 p-1.5"}>
                    <div className={"text-center text-sm"}>
                        <div className={"text-muted-foreground"}>
                            Maximum recording time is {Math.round(MAX_RECORDING_SECONDS / 60)} minutes
                        </div>
                        <div className={"font-semibold text-destructive"}>
                            recording will end in {secondsRemaining}s
                        </div>
                    </div>
                </PopoverContent>
                <PopoverTrigger>
                    <Button
                        className={
                            "rounded-2xl bg-red-800 outline outline-4 outline-red-800/20 transition-all duration-700 hover:bg-red-800/90"
                        }
                        onClick={() => transcription.stopRecording()}
                    >
                        <div className={"flex animate-pulse flex-col items-center gap-0.5"}>
                            <SquareIcon className={"scale h-4 w-4"} />
                            <div className={"w-8 text-xs font-semibold"}>
                                {DateTime.fromMillis(0)
                                    .set({ second: transcription.recordingSeconds })
                                    .toLocaleString({ minute: "numeric", second: "2-digit" })}
                            </div>
                        </div>
                    </Button>
                </PopoverTrigger>
            </Popover>
        );
    } else if (transcription.status === "connecting") {
        return (
            <Button className={"bg-red-800"} onClick={() => transcription.stopRecording()}>
                <Spinner />
            </Button>
        );
    } else {
        return (
            <Button disabled={props.disabled} onClick={() => void transcription.startRecording()}>
                <MicIcon className={"h-5 w-5 duration-500 animate-in zoom-in-75"} />
            </Button>
        );
    }
}

export type ChatInputBoxOnSend = (message: string, files: FileUpload[]) => void;

export function ChatInputBox({
    onSend,
    disabled,
    ...props
}: {
    onSend: ChatInputBoxOnSend;
    className?: string;
    disabled?: boolean;
}) {
    const [input, setInput] = useState("");
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const [recordingStream, setRecordingStream] = useState<MediaStream | null>(null);
    const [uploads, setUploads] = useState<FileUpload[]>([]);
    const [isDraggingFile, setIsDraggingFile] = useState(false);
    const uploadFileMutation = useUploadFileMutation();
    const deleteFileMutation = useDeleteFileMutation();
    const googleDrive = useGoogleDrive();

    useEffect(() => {
        const handleDragEnter = (e: DragEvent) => {
            if (e.dataTransfer?.types?.includes("Files")) {
                setIsDraggingFile(true);
            }
        };

        const handleDragLeave = (e: DragEvent) => {
            if (e.relatedTarget === null) {
                setIsDraggingFile(false);
            }
        };

        const handleDrop = () => {
            setIsDraggingFile(false);
        };

        window.addEventListener("dragenter", handleDragEnter);
        window.addEventListener("dragleave", handleDragLeave);
        window.addEventListener("drop", handleDrop);

        return () => {
            window.removeEventListener("dragenter", handleDragEnter);
            window.removeEventListener("dragleave", handleDragLeave);
            window.removeEventListener("drop", handleDrop);
        };
    }, []);

    const handleSend = async () => {
        const messageToSend = input.trim();

        if (messageToSend || uploads.length > 0) {
            onSend(messageToSend, uploads);
            setInput("");
            setUploads([]);
        }
    };

    function resizeTextArea(e: HTMLTextAreaElement) {
        e.style.height = "1px";
        e.style.height = e.scrollHeight + "px";
    }
    const onTextChange: EventHandler<ChangeEvent<HTMLTextAreaElement>> = (e) => {
        resizeTextArea(e.target);
        setInput(e.target.value);
    };
    function onRecorded(text: string) {
        setInput((previous) => (previous + text).trimStart());
        setTimeout(() => {
            if (textAreaRef.current) {
                resizeTextArea(textAreaRef.current);
            }
        }, 10);
    }

    const onKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
        if (e.key === "Enter") {
            const modKey = e.shiftKey || e.ctrlKey || e.metaKey || e.altKey;
            if (!modKey) {
                e.preventDefault();
                handleSend();
            }
        }
    };

    const uploadFiles = async (files: File[]) => {
        const newUploads = files.map((file) => ({
            id: crypto.randomUUID().toString(),
            name: file.name,
            uploading: true,
            mimeType: file.type,
        }));

        setUploads((prev) => [...prev, ...newUploads]);

        await Promise.all(
            newUploads.map(async (file) => {
                const result = await uploadFileMutation.mutateAsync({ file: files.find((f) => f.name === file.name)! });
                file.id = result.id;
                file.uploading = false;
            })
        );

        setUploads((uploads) => [...uploads]);
    };

    const handleFileDrop = (acceptedFiles: File[]) => uploadFiles(acceptedFiles);

    const handleFileInputChange = (e: Event) => {
        const files = Array.from((e.target as HTMLInputElement).files || []);
        const validSizeFiles = files.filter((file) => file.size <= MAX_FILE_SIZE);

        files.forEach((file) => {
            if (file.size > MAX_FILE_SIZE) {
                alert(`File "${file.name}" is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
            }
        });

        uploadFiles(validSizeFiles);
    };

    const handleRemoveFile = async (index: number) => {
        const fileToRemove = uploads[index];

        if (!fileToRemove.uploading && fileToRemove.id) {
            await deleteFileMutation.mutateAsync(fileToRemove.id);
        }

        setUploads((prev) => prev.filter((_, i) => i !== index));
    };

    const handleFileSelection = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = SUPPORTED_FILE_EXTENSIONS.join(",");
        input.multiple = true;
        input.onchange = handleFileInputChange;
        input.click();
    };

    const handleGoogleDrive = () => {
        googleDrive.openDrive((files) => {
            setUploads((prev) => [...prev, ...files]);
        });
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: handleFileDrop,
        accept: ACCEPTED_FILE_TYPES,
        multiple: true,
        noClick: true,
        maxSize: MAX_FILE_SIZE,
        onDropRejected: (rejectedFiles) => {
            rejectedFiles.forEach(({ file, errors }) => {
                if (errors.some((e) => e.code === "file-too-large")) {
                    alert(`File "${file.name}" is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
                }
            });
        },
    });

    return (
        <div className={cn("sticky bottom-0 flex flex-col bg-background/90", props.className)}>
            {!isDraggingFile && (
                <div className={"h-0 w-full overflow-visible"}>
                    <div
                        className={
                            "absolute -top-4 h-4 w-full bg-gradient-to-b from-transparent to-background opacity-65"
                        }
                    ></div>
                </div>
            )}
            <div
                {...getRootProps()}
                className={cn("flex flex-col gap-2", isDraggingFile && "rounded-lg p-2 ring-2 ring-primary")}
            >
                <Input {...getInputProps()} />

                {uploads.length > 0 && (
                    <AttachedFilesList
                        files={uploads.map((f) => ({ name: f.name, uploading: f.uploading, source: f.source }))}
                        onRemoveFile={handleRemoveFile}
                    />
                )}

                {isDraggingFile && <DragIndicator />}

                <div className="flex items-end gap-2">
                    <InputContainer>
                        <Textarea
                            disabled={disabled}
                            ref={textAreaRef}
                            className={"pr-18 h-10 max-h-36 min-h-10 resize-none backdrop-blur-lg"}
                            value={input}
                            onChange={onTextChange}
                            onKeyDown={onKeyDown}
                            placeholder="Type your message..."
                        />
                        <GoogleDriveButton
                            onClick={handleGoogleDrive}
                            disabled={disabled}
                            loading={googleDrive.isLoading}
                        />
                        <AttachButton onClick={handleFileSelection} disabled={disabled} />
                    </InputContainer>

                    <VoiceInputButton onRecorded={onRecorded} onStreamChange={setRecordingStream} disabled={disabled} />

                    <div className={"rounded-md backdrop-blur-lg"}>
                        <Button
                            onClick={handleSend}
                            disabled={
                                (!input.trim() && uploads.filter((f) => !f.uploading).length === 0) ||
                                disabled ||
                                recordingStream !== null
                            }
                        >
                            <SendIcon className={"me-2 h-5 w-5"} />
                            Send
                        </Button>
                    </div>
                </div>
                {recordingStream && (
                    <div className="flex h-8 flex-row items-center gap-2 rounded-md border bg-muted/50 px-3">
                        <MicIcon className="h-3.5 w-3.5 text-muted-foreground" />
                        <LiveWaveform
                            active={true}
                            stream={recordingStream}
                            mode="static"
                            height={20}
                            barWidth={2}
                            barGap={1}
                            barRadius={1}
                            sensitivity={1.2}
                            className="w-full flex-1"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
